import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';

import { AgFilterComponent } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';

import { BaasI18n } from '@lvflanders/bar';
import { _BaasWindow } from '@lvflanders/bar-ng';

export interface IBaasBooleanFilterParams extends IFilterParams {
    /** Overrides the text for the show all radio button. */
    showAllText?: string;
    /** Overrides the text for the true radio button. */
    trueText?: string;
    /** Overrides the text for the false radio button. */
    falseText?: string;
}

@Component({
    selector: 'baas-boolean-filter',
    template: `
        <mat-radio-group class="d-flex flex-column my-3" (ngModelChange)="onUpdateFilter()" [(ngModel)]="filter">
            <mat-radio-button class="m-1" [checked]="filter === null">{{ showAllText }}</mat-radio-button>
            <mat-radio-button class="m-1" [value]="true">{{ trueText }}</mat-radio-button>
            <mat-radio-button class="m-1" [value]="false">{{ falseText }}</mat-radio-button>
        </mat-radio-group>
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatRadioGroup, MatRadioButton, FormsModule]
})
/** @experimental Boolean filter which uses a radio button group to filter. */
export class BaasBooleanFilter implements AgFilterComponent {
    readonly #i18n = inject(BaasI18n);
    readonly #window = inject(_BaasWindow);

    filter: boolean = null;

    showAllText: string;
    trueText: string;
    falseText: string;

    #params: IBaasBooleanFilterParams;

    agInit(params: IBaasBooleanFilterParams): void {
        this.#params = params;
        this.showAllText = params.showAllText || this.#i18n.get('DOMAIN_BAR_AG_GRID.BOOLEAN_FILTER.SHOW_ALL');
        this.trueText = params.trueText || this.#i18n.get('DOMAIN_BAR_AG_GRID.BOOLEAN_FILTER.TRUE');
        this.falseText = params.falseText || this.#i18n.get('DOMAIN_BAR_AG_GRID.BOOLEAN_FILTER.FALSE');
    }

    isFilterActive(): boolean {
        return this.filter != null;
    }

    doesFilterPass(params: IDoesFilterPassParams): boolean {
        const colName = this.#params.column.getColId();
        return params.data[colName] == this.filter;
    }

    getModel() {
        if (this.filter != null) return { filterType: 'boolean', type: 'equals', checked: this.filter };
    }

    setModel(model: any) {
        this.filter = model?.checked;
    }

    onUpdateFilter() {
        this.#window.setTimeoutOutsideAngular(() => {
            this.#params.filterChangedCallback();
        }, 0);
    }
}
